/**
 * PERF-GPU-01 (audit 26-06-09, decision N-7, 2026-06-11) — GPU capability
 * detection. Current 45 MP imagery is 8,192 px wide — exactly AT the
 * max-texture cap of older Intel iGPUs; the next camera (Sony ILX-LR1,
 * 61 MP, ≈9,504 px) EXCEEDS it, and on such devices the browser silently
 * downscales. Until the DZI re-evaluation trigger (= ILX-LR1 adoption) the
 * platform must (a) detect the device capability, (b) make the quality
 * loss visible (the viewer-chrome warning consumes this util), and
 * (c) learn the real fleet's GPU population (the one structured
 * `[gpu-caps]` log line per session below is the telemetry-ready seam a
 * later sink — Sentry/Grafana, workplan 2.3 — consumes without rework).
 *
 * Detection runs ONCE per session (module-level memo — the value cannot
 * change within a session): a throwaway `webgl2 ?? webgl` context is
 * created, `MAX_TEXTURE_SIZE` read, the unmasked renderer captured when
 * `WEBGL_debug_renderer_info` exposes it, and the context explicitly lost
 * via `WEBGL_lose_context` so nothing leaks. WebGL-unavailable ⇒
 * `maxTextureSize: null` — unknown ≠ under-spec, so consumers show NO
 * warning, and the log line carries `renderer: "unknown"`.
 */

export interface GpuCapabilities {
  /** Device `MAX_TEXTURE_SIZE` in px, or `null` when WebGL is unavailable. */
  maxTextureSize: number | null;
  /** Which context answered (2 preferred), or `null` when unavailable. */
  webglVersion: 2 | 1 | null;
  /** Unmasked renderer string when exposable, else `null`. */
  renderer: string | null;
}

const UNKNOWN: GpuCapabilities = {
  maxTextureSize: null,
  webglVersion: null,
  renderer: null,
};

let cached: GpuCapabilities | null = null;

function detect(): GpuCapabilities {
  try {
    if (typeof document === "undefined") return UNKNOWN;
    const canvas = document.createElement("canvas");
    let webglVersion: 2 | 1 = 2;
    let gl = canvas.getContext("webgl2") as
      | WebGLRenderingContext
      | WebGL2RenderingContext
      | null;
    if (!gl) {
      webglVersion = 1;
      gl = canvas.getContext("webgl");
    }
    if (!gl) return UNKNOWN;

    const raw: unknown = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    const maxTextureSize =
      typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? raw : null;

    let renderer: string | null = null;
    try {
      // Chromium ≥ 110 exposes the unmasked string via the plain RENDERER
      // param too, but the debug extension is the cross-browser seam.
      const dbg = gl.getExtension("WEBGL_debug_renderer_info") as {
        UNMASKED_RENDERER_WEBGL: number;
      } | null;
      if (dbg) {
        const r: unknown = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL);
        renderer = typeof r === "string" ? r : null;
      }
    } catch {
      renderer = null;
    }

    try {
      // AC1 — no context leak: explicitly lose the throwaway context.
      (
        gl.getExtension("WEBGL_lose_context") as {
          loseContext: () => void;
        } | null
      )?.loseContext();
    } catch {
      // Losing the context is best-effort; GC reclaims it regardless.
    }

    return { maxTextureSize, webglVersion, renderer };
  } catch {
    return UNKNOWN;
  }
}

/**
 * Memoised GPU capability read. The first call performs the detection and
 * emits the single structured `[gpu-caps]` telemetry line (AC4); every
 * later call returns the cached value.
 */
export function getGpuCapabilities(): GpuCapabilities {
  if (cached) return cached;
  cached = detect();
  // AC4 — exactly ONE structured, telemetry-ready line per session.
  // eslint-disable-next-line no-console -- deliberate [gpu-caps] fleet-telemetry seam (PERF-GPU-01 AC4); a later sink consumes it without rework
  console.info("[gpu-caps]", {
    maxTextureSize: cached.maxTextureSize,
    webglVersion: cached.webglVersion,
    renderer: cached.renderer ?? "unknown",
  });
  return cached;
}

/** Test-only seam — drop the memo so unit tests can exercise each path. */
export function __resetGpuCapabilitiesForTest(): void {
  cached = null;
}
